import { useHead } from '@unhead/react';
import { Flex, Result } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { ConstructionSiteMismatchGuard } from 'business/quality-control/pages/record-segment-defect/components/construction-site-mismatch-guard';
import { LeftPanel } from 'business/quality-control/pages/record-segment-defect/components/left-panel';
import {
  BoardContextProvider,
  useBoardContext,
} from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { useMediaType } from 'technical/media/hooks';
import { ScreenOrientation } from 'technical/media/services/get-device-info';
import { NoMobileSupport } from 'ui/no-mobile-support';

import { RightPanel } from './components/right-panel';
import { SetComplianceModal } from './components/set-compliance-modal';
import { useBoardData } from './hooks/use-board-data';
import styles from './index.module.scss';

const RecordSegmentDefectContent = ({
  orientation,
  isMobile,
}: {
  orientation: ScreenOrientation;
  isMobile: boolean;
}) => {
  const { isDrawingEnable, boardConstructionSiteId } = useBoardContext();
  const { currentConstructionSiteId } = useAppContext();

  const [selectedRing, setSelectedRing] = useState<string | null>(null);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);

  const {
    ringInterval = { start: 0, end: 0 },
    rings = [],
    segmentDrawConfigs = [],
  } = useBoardData() ?? {};

  const isRightPanelMounted = rings.length === 0 || selectedRing !== null;

  if (isMobile) {
    return <NoMobileSupport />;
  }

  if (
    boardConstructionSiteId &&
    boardConstructionSiteId !== currentConstructionSiteId
  ) {
    return (
      <ConstructionSiteMismatchGuard
        boardConstructionSiteId={boardConstructionSiteId}
      />
    );
  }

  return (
    <Flex
      className={classNames(styles.page, 'page-appear')}
      style={{
        display:
          orientation === ScreenOrientation.Portrait ? 'none' : undefined,
      }}
    >
      <LeftPanel
        rings={rings}
        segmentDrawConfigs={segmentDrawConfigs}
        selectedRing={selectedRing}
        setSelectedRing={setSelectedRing}
        openComplianceModal={() => setComplianceModalOpen(true)}
      />
      {isRightPanelMounted && (
        <div style={{ display: isDrawingEnable ? 'none' : 'contents' }}>
          <RightPanel
            numberOfRings={rings.length}
            selectedRing={selectedRing}
            setSelectedRing={setSelectedRing}
          />
        </div>
      )}

      <SetComplianceModal
        ringInterval={ringInterval}
        isModalVisible={complianceModalOpen}
        closeModal={() => setComplianceModalOpen(false)}
      />
    </Flex>
  );
};

export const RecordSegmentDefectPage = () => {
  const { t } = useTranslation();
  const { isMobile, orientation } = useMediaType();

  useHead({
    title: t('user.menu.sections.qualityControl.recordSegmentDefect.title'),
  });

  return (
    <>
      <BoardContextProvider>
        <RecordSegmentDefectContent
          orientation={orientation}
          isMobile={isMobile}
        />
      </BoardContextProvider>
      {orientation === ScreenOrientation.Portrait && (
        <Result status="warning" title={t('errors.too_small_screen')} />
      )}
    </>
  );
};
